/** Request fan-out and identities for one-ship period comparison panels. */

import { runPlot } from '../../api/endpoints.ts';
import type { AnyPlotResponse, PlotRequest } from '../../api/types.ts';
import {
  periodComparison,
  resolveComparisonPeriods,
  type PlotPanelSpec,
} from '../../state/period-comparison.ts';
import { buildPlotRequest, plotRequestSignature, type PlotRequestOpts } from './plot-request.ts';
import { scopeRequestToShip } from './multi-ship-plot.ts';
import { userMessage } from '../../api/errors.ts';

export type PlotPanelStatus = 'loading' | 'ready' | 'empty' | 'error';

export interface PlotPanelResult {
  panel: PlotPanelSpec;
  requestSignature: string;
  status: PlotPanelStatus;
  data: AnyPlotResponse | null;
  error?: string;
}

function responseIsEmpty(data: AnyPlotResponse): boolean {
  if (data.plot_type === 'number') return data.value == null;
  if (data.plot_type === 'gauge') return !data.source_rows;
  if (data.plot_type === 'sankey') return !data.source_rows;
  if (data.plot_type === 'heatmap') return !data.counts?.length || data.x_edges.length < 2;
  if (data.plot_type === 'correlation') return !data.n || !data.values.length;
  if (data.plot_type === 'pie') return !data.slices?.length;
  if (data.plot_type === 'box') return !data.boxes?.length;
  if (data.plot_type === 'table') return !data.rows?.length;
  if (data.plot_type === 'histogram') return !data.series?.length || !data.edges?.length;
  if (data.plot_type === 'map') return !data.lats?.length;
  return !data.x?.length || !data.series?.length;
}

export function buildPeriodRequest(
  panel: PlotPanelSpec,
  opts?: Omit<PlotRequestOpts, 'ship' | 'start' | 'end'>,
): { body: PlotRequest; signature: string; error?: string } {
  const built = buildPlotRequest({
    ...opts,
    ship: panel.ship,
    start: panel.start,
    end: panel.end,
  });
  const scoped = scopeRequestToShip(built.body, panel.ship);
  return {
    body: scoped.body,
    signature: plotRequestSignature(scoped.body),
    ...(scoped.skip ? { error: scoped.skip } : {}),
  };
}

/** Labels are presentation-only; panel identity/order and request bodies dirty data. */
export function periodRequestSignature(
  panels = resolveComparisonPeriods(),
  opts?: Omit<PlotRequestOpts, 'ship' | 'start' | 'end'>,
): string {
  return JSON.stringify(
    panels.map((panel) => {
      const built = buildPeriodRequest(panel, opts);
      return { id: panel.id, request: built.signature, error: built.error ?? null };
    }),
  );
}

export async function fetchPeriodPanel(
  panel: PlotPanelSpec,
  opts?: Omit<PlotRequestOpts, 'ship' | 'start' | 'end'>,
): Promise<PlotPanelResult> {
  const built = buildPeriodRequest(panel, opts);
  if (built.error) {
    return {
      panel: { ...panel },
      requestSignature: built.signature,
      status: 'error',
      data: null,
      error: built.error,
    };
  }
  try {
    const data = await runPlot(built.body);
    return {
      panel: { ...panel },
      requestSignature: built.signature,
      status: responseIsEmpty(data) ? 'empty' : 'ready',
      data,
    };
  } catch (error) {
    return {
      panel: { ...panel },
      requestSignature: built.signature,
      status: 'error',
      data: null,
      error: userMessage(error),
    };
  }
}

/**
 * Fetch at most two distinct requests at once. Identical immutable requests
 * share one response while retaining separate labeled slots.
 */
export async function fetchPeriodPlots(
  panels = resolveComparisonPeriods(),
  opts?: Omit<PlotRequestOpts, 'ship' | 'start' | 'end'>,
  onProgress?: (results: PlotPanelResult[], completed: number) => void,
): Promise<PlotPanelResult[]> {
  const results: PlotPanelResult[] = panels.map((panel) => ({
    panel: { ...panel },
    requestSignature: buildPeriodRequest(panel, opts).signature,
    status: 'loading',
    data: null,
  }));
  const shared = new Map<string, Promise<PlotPanelResult>>();
  let cursor = 0;
  let completed = 0;

  const worker = async (): Promise<void> => {
    while (cursor < panels.length) {
      const index = cursor++;
      const panel = panels[index]!;
      const built = buildPeriodRequest(panel, opts);
      const key = built.error ? `${panel.id}:${built.error}` : built.signature;
      let pending = shared.get(key);
      if (!pending) {
        pending = fetchPeriodPanel(panel, opts);
        shared.set(key, pending);
      }
      const fetched = await pending;
      results[index] = {
        ...fetched,
        panel: { ...panel },
        // The request is identical even when the slot label/id differs.
        requestSignature: built.signature,
      };
      completed++;
      onProgress?.(results.map((result) => ({ ...result, panel: { ...result.panel } })), completed);
    }
  };

  await Promise.all(Array.from({ length: Math.min(2, panels.length) }, () => worker()));
  return results;
}

/** True only while a supported, serializable comparison is active. */
export function hasActivePeriodComparison(): boolean {
  return !!periodComparison.value;
}
